/** Shared weighing-scale protocol helpers (Electron + Web Serial). */

export type ScaleProtocol =
  | 'generic_continuous'
  | 'nci'
  | 'cas_stx_etx'
  | 'mettler_continuous'
  | 'keyboard_wedge';

export type ScaleBrandPreset =
  | 'none'
  | 'generic'
  | 'cas'
  | 'avery'
  | 'brecknell'
  | 'mettler'
  | 'dibal'
  | 'adams'
  | 'jadever_aclas'
  | 'datalogic'
  | 'keyboard_wedge';

export interface ScaleReading {
  kg: number;
  stable?: boolean;
}

export interface ScaleBrandOption {
  id: ScaleBrandPreset;
  label: string;
  protocol: ScaleProtocol;
  baudRate: number;
}

export const SCALE_BRAND_PRESETS: ScaleBrandOption[] = [
  { id: 'none', label: 'None', protocol: 'generic_continuous', baudRate: 9600 },
  { id: 'generic', label: 'Generic continuous', protocol: 'generic_continuous', baudRate: 9600 },
  { id: 'cas', label: 'CAS (PD / PDN / clones)', protocol: 'cas_stx_etx', baudRate: 9600 },
  { id: 'avery', label: 'Avery Weigh-Tronix / Avery Berkel', protocol: 'nci', baudRate: 9600 },
  { id: 'brecknell', label: 'Salter Brecknell', protocol: 'nci', baudRate: 9600 },
  { id: 'mettler', label: 'Mettler Toledo', protocol: 'mettler_continuous', baudRate: 9600 },
  { id: 'dibal', label: 'Dibal', protocol: 'nci', baudRate: 9600 },
  { id: 'adams', label: 'Adams', protocol: 'generic_continuous', baudRate: 9600 },
  { id: 'jadever_aclas', label: 'Jadever / Aclas (generic)', protocol: 'generic_continuous', baudRate: 9600 },
  { id: 'datalogic', label: 'Datalogic scanner-scale', protocol: 'nci', baudRate: 9600 },
  { id: 'keyboard_wedge', label: 'Keyboard wedge', protocol: 'keyboard_wedge', baudRate: 9600 },
];

export const SCALE_PROTOCOL_OPTIONS: { id: ScaleProtocol; label: string }[] = [
  { id: 'generic_continuous', label: 'Generic continuous ASCII' },
  { id: 'nci', label: 'NCI / Weigh-Tronix (poll)' },
  { id: 'cas_stx_etx', label: 'CAS STX/ETX' },
  { id: 'mettler_continuous', label: 'Mettler Toledo continuous' },
  { id: 'keyboard_wedge', label: 'Keyboard wedge' },
];

export function presetDefaults(preset: ScaleBrandPreset): Pick<ScaleBrandOption, 'protocol' | 'baudRate'> {
  const row = SCALE_BRAND_PRESETS.find((p) => p.id === preset);
  return {
    protocol: row?.protocol ?? 'generic_continuous',
    baudRate: row?.baudRate ?? 9600,
  };
}

/** NCI weight request */
export const NCI_POLL_BYTES = new TextEncoder().encode('W\r');

function parseSignedNumber(raw: string): number | null {
  const n = parseFloat(raw.replace(',', '.'));
  if (Number.isNaN(n) || n < 0) return null;
  return n;
}

/** Generic / Adams / Jadever continuous ASCII */
export function parseGenericContinuous(line: string): ScaleReading | null {
  if (!line) return null;
  const cleaned = String(line).trim();
  const m =
    cleaned.match(/([+-]?\d+[.,]\d+)\s*kg/i) ||
    cleaned.match(/(?:W[:\s]|GS[,:\s]|NT[,:\s])\s*([+-]?\d+[.,]\d+)/i) ||
    cleaned.match(/([+-]?\d+[.,]\d+)/);
  if (!m) return null;
  const kg = parseSignedNumber(m[1]);
  if (kg == null) return null;
  const unstable = /\bUS\b|unstable|motion/i.test(cleaned);
  const stable = /\bST\b|\bGS\b|stable/i.test(cleaned) ? true : unstable ? false : undefined;
  return { kg, stable };
}

/** Mettler continuous e.g. ST,GS,+0001.500kg */
export function parseMettlerContinuous(line: string): ScaleReading | null {
  if (!line) return null;
  const cleaned = String(line).trim();
  const m = cleaned.match(/([+-]?\d+[.,]\d+)\s*kg/i) || cleaned.match(/([+-]?\d+[.,]\d{3,})/);
  if (!m) return null;
  const kg = parseSignedNumber(m[1]);
  if (kg == null) return null;
  let stable: boolean | undefined;
  if (/^ST\b|,ST,|\bST,/i.test(cleaned) || /\bST\b/i.test(cleaned.split(',')[0] || '')) {
    stable = true;
  }
  if (/\bUS\b|^US\b/i.test(cleaned)) stable = false;
  if (/\bGS\b|\bNT\b/i.test(cleaned) && stable === undefined) stable = true;
  return { kg, stable };
}

/** NCI-style reply after W poll */
export function parseNciResponse(line: string): ScaleReading | null {
  if (!line) return null;
  const cleaned = String(line).trim();
  // Common: "  1.234 kg" or "S  1.234kg" or "GS0.500kg"
  const m =
    cleaned.match(/([+-]?\d+[.,]\d+)\s*kg/i) ||
    cleaned.match(/^[A-Z]*\s*([+-]?\d+[.,]\d+)/i) ||
    cleaned.match(/([+-]?\d+[.,]\d+)/);
  if (!m) return null;
  const kg = parseSignedNumber(m[1]);
  if (kg == null) return null;
  const stable = !/\bU\b|unstable|motion/i.test(cleaned);
  return { kg, stable };
}

/**
 * CAS STX/ETX frame. Buffer may contain partial data; returns reading + leftover.
 * Typical: STX + status + sign + weight digits + ETX (+ optional BCC)
 */
export function parseCasStxEtx(buffer: string): { reading: ScaleReading | null; rest: string } {
  const STX = '\x02';
  const ETX = '\x03';
  let rest = buffer;
  let reading: ScaleReading | null = null;

  while (true) {
    const start = rest.indexOf(STX);
    if (start < 0) {
      // Keep last few chars in case STX arrives later
      rest = rest.slice(-8);
      break;
    }
    const end = rest.indexOf(ETX, start + 1);
    if (end < 0) {
      rest = rest.slice(start);
      break;
    }
    const frame = rest.slice(start + 1, end);
    rest = rest.slice(end + 1);
    // Frame often: status(1) + sign(1) + weight digits
    const m = frame.match(/([+-]?)(\d+[.,]?\d*)/);
    if (!m) continue;
    const sign = m[1] === '-' ? -1 : 1;
    const kg = parseSignedNumber(m[2]);
    if (kg == null) continue;
    const status = frame.charAt(0);
    const stable = status === 'S' || status === '0' || status === ' ' ? true : status === 'U' ? false : undefined;
    reading = { kg: Math.abs(kg) * (sign < 0 ? 0 : 1), stable };
    // Prefer last complete frame in this chunk
  }

  return { reading, rest };
}

export function parseScaleChunk(
  protocol: ScaleProtocol,
  chunk: string,
  frameBuffer: string,
): { reading: ScaleReading | null; frameBuffer: string; linesConsumed?: string[] } {
  if (protocol === 'keyboard_wedge') {
    return { reading: null, frameBuffer: '' };
  }

  if (protocol === 'cas_stx_etx') {
    const { reading, rest } = parseCasStxEtx(frameBuffer + chunk);
    return { reading, frameBuffer: rest };
  }

  const combined = frameBuffer + chunk;
  const parts = combined.split(/[\r\n]+/);
  const leftover = parts.pop() || '';
  let reading: ScaleReading | null = null;
  for (const line of parts) {
    let r: ScaleReading | null = null;
    if (protocol === 'mettler_continuous') r = parseMettlerContinuous(line);
    else if (protocol === 'nci') r = parseNciResponse(line);
    else r = parseGenericContinuous(line);
    if (r) reading = r;
  }
  return { reading, frameBuffer: leftover, linesConsumed: parts };
}

/** Map legacy stored protocol labels to ids */
export function normalizeProtocol(raw: string | undefined | null): ScaleProtocol {
  if (!raw) return 'generic_continuous';
  const v = String(raw).trim().toLowerCase();
  if (v === 'generic_continuous' || v.includes('generic')) return 'generic_continuous';
  if (v === 'nci' || v.includes('nci') || v.includes('weigh-tronix')) return 'nci';
  if (v === 'cas_stx_etx' || v.includes('cas')) return 'cas_stx_etx';
  if (v === 'mettler_continuous' || v.includes('mettler')) return 'mettler_continuous';
  if (v === 'keyboard_wedge' || v.includes('keyboard') || v.includes('wedge')) return 'keyboard_wedge';
  return 'generic_continuous';
}
